import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { Reveal } from "@/components/site/Reveal";
import { SectionHeading } from "@/components/site/Primitives";
import { cn } from "@/lib/utils";

const FILTERS = ["All", "Monaco", "Geneva", "Paris", "Recruitment", "Talent Development", "Consulting"];

export const FeaturedCaseStudies = ({ caseStudies = [] }) => {
  const [filter, setFilter] = useState("All");
  const reduce = useReducedMotion();
  const filtered = useMemo(() => {
    if (filter === "All") return caseStudies;
    return caseStudies.filter((c) => c.market === filter || c.service === filter);
  }, [filter, caseStudies]);

  return (
    <section className="section-py bg-[color:var(--bp-cream)]" data-testid="case-studies-section">
      <div className="container-bp">
        <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
          <SectionHeading tone="light" eyebrow="Proof, quantified" title="Mandates delivered — with the numbers to show for it." />
          <Link to="/case-studies" className="inline-flex shrink-0 items-center gap-2 text-[13px] font-semibold text-[color:var(--bp-teal)]">
            View all case studies <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>

        {/* Filters — horizontally scrollable on mobile, wraps on larger screens */}
        <Reveal>
          <div className="bp-scroll-x mt-8 -mx-[var(--gutter)] flex gap-2 overflow-x-auto px-[var(--gutter)] pb-1 sm:mx-0 sm:flex-wrap sm:overflow-visible sm:px-0">
            {FILTERS.map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                data-testid={`case-filter-${f.toLowerCase().replace(/\s+/g, "-")}`}
                className={cn(
                  "shrink-0 whitespace-nowrap rounded-full border px-4 py-2 text-[12.5px] font-medium transition-colors",
                  filter === f
                    ? "border-[color:var(--bp-teal)] bg-[color:var(--bp-teal)] text-white"
                    : "border-[#E2DCCF] text-[color:var(--bp-text-body-on-light)] hover:border-[color:var(--bp-teal)] hover:text-[color:var(--bp-teal)]"
                )}
              >
                {f}
              </button>
            ))}
          </div>
        </Reveal>

        {filtered.length === 0 ? (
          <p className="mt-10 text-[14px] text-[color:var(--bp-text-body-on-light)]">No case studies in this category yet.</p>
        ) : (
          <motion.div
            key={filter}
            initial={reduce ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4 }}
            className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3"
          >
            {filtered.map((c, i) => (
              <motion.div
                key={c.slug}
                initial={reduce ? false : { opacity: 0, y: 22 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1], delay: Math.min(i * 0.07, 0.4) }}
              >
                <Link
                  to={`/case-studies/${c.slug}`}
                  data-testid={`case-card-${c.slug}`}
                  className="group flex h-full flex-col overflow-hidden rounded-[var(--radius-lg)] border border-[#E6E0D4] bg-white transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_24px_60px_rgba(10,31,61,0.14)]"
                >
                  <div className="relative aspect-[16/10] overflow-hidden">
                    <img src={c.image} alt={c.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                    <div className="absolute left-3 top-3 flex flex-wrap gap-2">
                      <span className="rounded-full bg-[color:var(--bp-bg)]/85 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.1em] text-[color:var(--bp-teal)] backdrop-blur">{c.market}</span>
                      <span className="rounded-full bg-[color:var(--bp-bg)]/85 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.1em] text-[color:var(--bp-cream)] backdrop-blur">{c.service}</span>
                    </div>
                  </div>
                  <div className="flex flex-1 flex-col p-6">
                    <h3 className="font-display text-lg font-semibold leading-snug text-[color:var(--bp-text-on-light)]">{c.title}</h3>
                    <p className="mt-2 flex-1 text-[13.5px] leading-relaxed text-[color:var(--bp-text-body-on-light)]">{c.summary}</p>
                    <div className="mt-5 flex items-center gap-5 border-t border-[#EFE9DD] pt-4">
                      {c.metrics.slice(0, 2).map((m) => (
                        <div key={m.label}>
                          <p className="font-display text-lg font-semibold text-[color:var(--bp-teal)]">{m.value}</p>
                          <p className="text-[11px] leading-tight text-[color:var(--bp-text-body-on-light)]">{m.label}</p>
                        </div>
                      ))}
                    </div>
                    <span className="mt-4 inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-[color:var(--bp-teal)]">
                      Read case study <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
                    </span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </motion.div>
        )}
      </div>
    </section>
  );
};
